import type { NestedFilter } from '@@partner:types:graphql'
import type { BackgroundTaskItem } from '@@partner:graphql/query/backgroundTask/allBackgroundTasks.gql'

import { BACKEND_URL } from '@@partner:config/api'

export interface ExportProductsFilter {
  ids?: number[]
  statuses?: number[]
  categoryIds?: number[]
  searchTerms?: string[]
  nameStartsWith?: string[]
  withPhoto?: boolean
  updatedFrom?: string
  updatedTo?: string
  shopIds?: number[]
}

interface NestedFilterNode {
  and?: NestedFilterNode[]
  or?: NestedFilterNode[]
  fields?: string[]
  op?: string
  value?: Array<string | number | boolean>
}

const normalizeNumberList = (input?: Array<string | number | boolean>) => {
  if (!Array.isArray(input)) {
    return []
  }

  return Array.from(new Set(
    input
      .map((item) => Number(item))
      .filter((value) => Number.isInteger(value) && value > 0)
  ))
}

const normalizeNonNegativeNumberList = (input?: Array<string | number | boolean>) => {
  if (!Array.isArray(input)) {
    return []
  }

  return Array.from(new Set(
    input
      .map((item) => Number(item))
      .filter((value) => Number.isInteger(value) && value >= 0)
  ))
}

const normalizeDateString = (input: unknown): string | undefined => {
  if (typeof input === 'string' && input.length > 0) {
    return input
  }

  if (typeof input === 'number' && Number.isFinite(input)) {
    return new Date(input).toISOString()
  }

  return undefined
}

const normalizeStringList = (input?: Array<string | number | boolean>) => {
  if (!Array.isArray(input)) {
    return []
  }

  return Array.from(new Set(
    input
      .map((item) => String(item ?? '').trim())
      .filter((value) => value.length > 0)
  ))
}

export const buildExportFiltersFromNestedFilter = (filter: NestedFilter): ExportProductsFilter => {
  const ids = new Set<number>()
  const statuses = new Set<number>()
  const categoryIds = new Set<number>()
  const searchTerms = new Set<string>()
  const nameStartsWith = new Set<string>()

  let withPhoto: boolean | undefined
  let updatedFrom: string | undefined
  let updatedTo: string | undefined

  const walk = (node?: NestedFilterNode) => {
    if (!node || typeof node !== 'object') {
      return
    }

    if (Array.isArray(node.and)) {
      node.and.forEach(walk)
    }
    if (Array.isArray(node.or)) {
      node.or.forEach(walk)
    }

    const field = node.fields?.[0]
    const value = node.value

    if (!field || !node.op) {
      return
    }

    if (field === 'id' && node.op === 'contains') {
      normalizeNumberList(value).forEach((id) => ids.add(id))
      return
    }

    if (field === 'status' && node.op === 'contains') {
      normalizeNonNegativeNumberList(value).forEach((status) => statuses.add(status))
      return
    }

    if (field === 'categoryIds' && node.op === 'contains') {
      normalizeNumberList(value).forEach((categoryId) => categoryIds.add(categoryId))
      return
    }

    if ((field === 'xid' || field === 'article_num' || field === 'ean') && node.op === 'contains') {
      normalizeStringList(value).forEach((term) => searchTerms.add(term))
      return
    }

    if (field === 'name' && node.op === 'starts_with') {
      normalizeStringList(value).forEach((term) => nameStartsWith.add(term))
      return
    }

    if (field === 'preview_id') {
      if (node.op === 'not_null') {
        withPhoto = true
      }
      if (node.op === 'is_null') {
        withPhoto = false
      }
      return
    }

    if (field === 'updated_at' && Array.isArray(value) && value.length > 0) {
      if (node.op === 'between') {
        updatedFrom = normalizeDateString(value[0])
        updatedTo = normalizeDateString(value[1])
      }

      if (node.op === 'gte' || node.op === 'gt') {
        updatedFrom = normalizeDateString(value[0])
      }

      if (node.op === 'lte' || node.op === 'lt') {
        updatedTo = normalizeDateString(value[0])
      }
    }
  }

  walk(filter as NestedFilterNode)

  const result: ExportProductsFilter = {}

  if (ids.size > 0) {
    result.ids = Array.from(ids)
  }
  if (statuses.size > 0) {
    result.statuses = Array.from(statuses)
  }
  if (categoryIds.size > 0) {
    result.categoryIds = Array.from(categoryIds)
  }
  if (searchTerms.size > 0) {
    result.searchTerms = Array.from(searchTerms)
  }
  if (nameStartsWith.size > 0) {
    result.nameStartsWith = Array.from(nameStartsWith)
  }
  if (typeof withPhoto === 'boolean') {
    result.withPhoto = withPhoto
  }
  if (updatedFrom) {
    result.updatedFrom = updatedFrom
  }
  if (updatedTo) {
    result.updatedTo = updatedTo
  }

  return result
}

const extractFileNameFromPath = (filePath?: string | null): string | null => {
  if (!filePath || typeof filePath !== 'string') {
    return null
  }

  const normalized = filePath.replace(/\\/g, '/')
  const chunks = normalized.split('/')
  const name = chunks[chunks.length - 1]

  return name || null
}

export const resolveTaskExportFileName = (task: BackgroundTaskItem): string => {
  const result = task.result as Record<string, any> | null | undefined

  if (typeof result?.fileName === 'string' && result.fileName.trim().length > 0) {
    return result.fileName
  }

  const fileNameFromPath = extractFileNameFromPath(result?.filePath)
  if (fileNameFromPath) {
    return fileNameFromPath
  }

  return `products_export_${task.id}.xlsx`
}

export const resolveTaskDownloadUrl = (task: BackgroundTaskItem, fileName: string): string | null => {
  const result = task.result as Record<string, any> | null | undefined

  if (typeof result?.downloadUrl === 'string' && result.downloadUrl.trim().length > 0) {
    return result.downloadUrl
  }

  const taskId = Number(task.id)
  if (!Number.isInteger(taskId) || taskId <= 0) {
    return null
  }

  return `/api/files/download/job-${taskId}/${encodeURIComponent(fileName)}`
}

export const toBackendAbsoluteUrl = (url: string): string => {
  if (/^https?:\/\//i.test(url)) {
    return url
  }

  const normalizedBackendUrl = BACKEND_URL.replace(/\/$/, '')
  const normalizedPath = url.startsWith('/') ? url : `/${url}`

  return `${normalizedBackendUrl}${normalizedPath}`
}

export const formatTaskDateTime = (value?: string | null): string => {
  if (!value) {
    return '—'
  }

  const date = new Date(value)
  if (isNaN(date.getTime())) {
    return value
  }

  return date.toLocaleString('ru-RU')
}

export const TASK_STATUS_LABELS: Record<string, string> = {
  pending: 'В очереди',
  processing: 'В работе',
  completed: 'Завершено',
  failed: 'Ошибка'
}

export const TASK_STATUS_COLORS: Record<string, 'yellow' | 'blue' | 'green' | 'red' | 'cyan'> = {
  pending: 'yellow',
  processing: 'blue',
  completed: 'green',
  failed: 'red'
}
